import { useRef, useState } from 'react'
import { Alert, AuthLayout, Button, Link, PasswordField, TextField } from 'armxlib'
import { useNavigate } from 'react-router'
import { PageMeta } from '../../app/PageMeta'
import { en } from '../../i18n/en'
import { MIN_PASSWORD_LENGTH } from '../../lib/rules'
import { useCaptcha } from '../../lib/turnstile'
import { ROUTES } from '../../routes'
import { SiteFooter } from '../legal/SiteFooter'
import { failureReason, requestPasswordReset, updatePassword, verifyRecoveryCode } from './api'
import { CodeStep } from './CodeStep'

const t = en.auth

/**
 * `/reset-password`: email and Turnstile, then the code received by email and
 * the new password. The code signs the user in; the password is changed next.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const verified = useRef(false)
  const captcha = useCaptcha()

  async function requestCode() {
    if (!captcha.token) {
      setError(captcha.failed ? t.captchaFailed : t.captchaPending)
      return
    }
    setPending(true)
    setError(null)
    try {
      await requestPasswordReset(email, captcha.token)
      setStep('code')
    } catch (failure) {
      setError(t.errors[failureReason(failure)])
    } finally {
      captcha.reset()
      setPending(false)
    }
  }

  // A code works once: if the new password is refused, retry without it.
  async function changePassword(code: string) {
    if (!verified.current) {
      await verifyRecoveryCode(email, code)
      verified.current = true
    }
    await updatePassword(password)
    navigate(ROUTES.chat.path, { replace: true })
  }

  const meta = <PageMeta title={t.reset.title} />

  if (step === 'code') {
    return (
      <AuthLayout
        brand={en.site.name}
        title={t.reset.codeTitle}
        description={t.reset.codeDescription(email)}
        footer={<SiteFooter />}
      >
        {meta}
        <CodeStep
          submitLabel={t.reset.confirm}
          canSubmit={password.length >= MIN_PASSWORD_LENGTH}
          onSubmit={changePassword}
          onResend={(captchaToken) => requestPasswordReset(email, captchaToken)}
        >
          <PasswordField
            label={t.newPassword}
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint={t.passwordHint(MIN_PASSWORD_LENGTH)}
          />
        </CodeStep>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      brand={en.site.name}
      title={t.reset.title}
      description={t.reset.description}
      footer={
        <>
          <Link href={ROUTES.login.path}>{t.reset.backToSignIn}</Link>
          <SiteFooter />
        </>
      }
    >
      {meta}
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void requestCode()
        }}
      >
        {error && <Alert tone="danger">{error}</Alert>}
        <TextField
          label={t.email}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        {captcha.widget}
        <Button type="submit" variant="primary" fullWidth loading={pending}>
          {t.reset.submit}
        </Button>
      </form>
    </AuthLayout>
  )
}
